import { useRef, useState } from "react";
import api, { API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X, Film, Images } from "lucide-react";

// kind: "image" | "video"; publicMode -> /upload/public (images only, no library);
// library -> show "Library" picker (admin-only, reuses /admin/media)
export default function ImageUpload({ value, onChange, label = "Image", testid = "image", kind = "image", publicMode = false, library = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const isVideo = kind === "video";
  const maxMb = isVideo ? 200 : publicMode ? 5 : 8;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const typeOk = isVideo ? file.type.startsWith("video/") : file.type.startsWith("image/");
    if (!typeOk) { toast.error(`Please choose a ${isVideo ? "video" : "image"} file`); return; }
    if (file.size > maxMb * 1024 * 1024) { toast.error(`File must be under ${maxMb}MB`); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = publicMode ? "/upload/public" : "/upload";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${API}/files/${data.path}`);
      toast.success(`${isVideo ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setLibLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      const filtered = data.filter((m) => (m.content_type || "").startsWith(isVideo ? "video/" : "image/"));
      setLibItems(filtered);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLibLoading(false); }
  };

  const pick = (m) => { onChange(`${API}/files/${m.storage_path}`); setPickerOpen(false); };

  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">{label}</label>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 shrink-0 border-2 border-[#121212] bg-white overflow-hidden flex items-center justify-center">
          {value ? (
            isVideo ? (
              <video src={value} className="w-full h-full object-cover" data-testid={`${testid}-preview`} muted />
            ) : (
              <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
            )
          ) : (
            isVideo ? <Film size={20} className="text-[#121212]/30" /> : <span className="text-[#121212]/30 text-xs">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            ref={inputRef} type="file"
            accept={isVideo ? "video/*" : "image/*"}
            onChange={handleFile} className="hidden" data-testid={`${testid}-file-input`}
          />
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              data-testid={`${testid}-upload-btn`}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.2em] uppercase font-bold"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Uploading…" : isVideo ? "Upload video" : "Upload image"}
            </button>
            {library && !publicMode && (
              <button
                type="button"
                data-testid={`${testid}-library-btn`}
                onClick={openPicker}
                className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#121212] hover:bg-[#C6FF33] text-xs tracking-[0.2em] uppercase font-bold"
              >
                <Images size={14} /> Library
              </button>
            )}
            {value && (
              <button type="button" data-testid={`${testid}-clear-btn`} onClick={() => onChange("")} className="p-2.5 border-2 border-[#121212] hover:bg-[#FF8F80]">
                <X size={14} />
              </button>
            )}
          </div>
          <input
            data-testid={`${testid}-url-input`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={isVideo ? "…or paste a video URL" : "…or paste an image URL"}
            className="w-full px-3 py-2 text-sm border-2 border-[#121212]/30 bg-white focus:outline-none focus:border-[#121212]"
          />
        </div>
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setPickerOpen(false)} data-testid={`${testid}-library-modal`}>
          <div className="w-full max-w-3xl max-h-[80vh] overflow-auto bg-[#F2E9D8] border-2 border-[#121212] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif font-black text-2xl text-[#121212]">Choose from library</h3>
              <button onClick={() => setPickerOpen(false)} className="p-1 hover:bg-[#121212]/10"><X size={20} /></button>
            </div>
            {libLoading ? (
              <p className="text-[#121212]/60 py-10 text-center">Loading…</p>
            ) : libItems.length === 0 ? (
              <p className="text-[#121212]/60 py-10 text-center">No {isVideo ? "videos" : "images"} in your library yet. Upload one above.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {libItems.map((m) => {
                  const url = `${API}/files/${m.storage_path}`;
                  return (
                    <button key={m.id} type="button" data-testid={`${testid}-lib-item-${m.id}`} onClick={() => pick(m)} className="aspect-square border-2 border-[#121212] overflow-hidden hover:ring-4 hover:ring-[#C6FF33] transition-all">
                      {isVideo ? <video src={url} className="w-full h-full object-cover" muted /> : <img src={url + "?w=300"} alt={m.original_filename} className="w-full h-full object-cover" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
